import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Dialog, DialogActions } from '../../../design-system/Overlay';

/**
 * Petites boîtes de l'éditeur v2, au style commun (principale à droite, Annuler à sa gauche, destructive en rouge
 * à gauche, X en haut) : « Nom de la copie » (planches « EditeurV2-Copier », « -Couches »), suppression d'une page
 * avec ou sans ses champs (« EditeurV2-Pages »).
 */

/** Proposition de nom d'une copie : « Fiche (copie) », puis « Fiche (copie 2) » si elle existe. */
export function copyName(name: string, taken: readonly string[] = []): string {
  const base = `${name} (copie)`;
  if (!taken.includes(base)) return base;
  let index = 2;
  while (taken.includes(`${name} (copie ${index})`)) index += 1;
  return `${name} (copie ${index})`;
}

export function NameDialog({ title, subtitle, label = 'Nom de la copie', proposal, confirm = 'Créer', onClose, onConfirm }: { title: string; subtitle?: string; label?: string; proposal: string; confirm?: string; onClose: () => void; onConfirm: (name: string) => void }) {
  const id = useId();
  const [name, setName] = useState(proposal);
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    // La proposition est sélectionnée : taper la remplace.
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const ok = name.trim() !== '';
  return (
    <Dialog
      open
      size="sm"
      title={title}
      {...(subtitle === undefined ? {} : { subtitle })}
      onClose={onClose}
      footer={<DialogActions cancel={{ label: 'Annuler', onClick: onClose }} primary={{ label: confirm, disabled: !ok, onClick: () => ok && onConfirm(name.trim()) }} />}
    >
      <form className="ay-props__row" onSubmit={(event) => { event.preventDefault(); if (ok) onConfirm(name.trim()); }}>
        <label htmlFor={id} className="ay-field__label">{label}</label>
        <input ref={ref} id={id} type="text" value={name} onChange={(event) => setName(event.target.value)} />
      </form>
    </Dialog>
  );
}

/** Supprimer une page : ses champs vont « À placer », sauf si l'on coche « Supprimer aussi ses N champs ». */
export function DeletePageDialog({ name, position, fields, onClose, onConfirm }: { name: string; position: string; fields: ReadonlyArray<string>; onClose: () => void; onConfirm: (withFields: boolean) => void }) {
  const id = useId();
  const [withFields, setWithFields] = useState(false);
  return (
    <Dialog
      open
      size="sm"
      title={`Supprimer « ${name} » ?`}
      subtitle={position}
      onClose={onClose}
      footer={<DialogActions destructive={{ label: 'Supprimer', onClick: () => onConfirm(withFields) }} cancel={{ label: 'Annuler', onClick: onClose }} />}
    >
      {fields.length > 0 && (
        <ul className="ay-v2-chips" aria-label="Champs de la page">
          {fields.map((field) => <li key={field} className="ay-v2-pill">{field}</li>)}
        </ul>
      )}
      {fields.length > 0 && (
        <div className="ay-props__row ay-props__row--inline">
          <input id={id} type="checkbox" checked={withFields} onChange={(event) => setWithFields(event.target.checked)} />
          <label htmlFor={id} className="ay-field__label">{fields.length > 1 ? `Supprimer aussi ses ${fields.length} champs` : 'Supprimer aussi son champ'}</label>
        </div>
      )}
    </Dialog>
  );
}

/** Ligne « libellé : valeur » d'une boîte d'impacts. */
export function ImpactLine({ label, detail, tone }: { label: ReactNode; detail: ReactNode; tone?: 'follows' | 'keeps' | 'neutral' }) {
  return (
    <li className={`ay-v2-impact is-${tone ?? 'neutral'}`}>
      <span>{label}</span>
      <span className="ay-v2-impact__detail">{detail}</span>
    </li>
  );
}
